import React, { startTransition, useDeferredValue, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaArrowRight,
  FaEnvelope,
  FaFacebook,
  FaGithub,
  FaLinkedin,
  FaPlayCircle,
  FaTiktok,
  FaYoutube,
} from 'react-icons/fa';
import ChatbotPanel from '../components/ChatbotPanel';
import ItemCard from '../components/ItemCard';
import SectionHeader from '../components/SectionHeader';
import useSiteSeo from '../hooks/useSiteSeo';

const socialIcons = {
  github: <FaGithub aria-hidden="true" />,
  linkedin: <FaLinkedin aria-hidden="true" />,
  youtube: <FaYoutube aria-hidden="true" />,
  tiktok: <FaTiktok aria-hidden="true" />,
  facebook: <FaFacebook aria-hidden="true" />,
};

function PublicPortfolioPage({ portfolio, loading, error, onRetry, onSendChat }) {
  const location = useLocation();
  const [collectionFilter, setCollectionFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);

  useSiteSeo({
    portfolio,
    pathname: location.pathname,
  });

  const collections = useMemo(
    () => ['All', ...(portfolio?.taxonomies?.collections || [])],
    [portfolio]
  );

  const statuses = useMemo(
    () => ['All', ...(portfolio?.taxonomies?.statuses || [])],
    [portfolio]
  );

  const filteredItems = useMemo(() => {
    if (!portfolio) {
      return [];
    }

    const query = deferredSearch.trim().toLowerCase();

    return portfolio.items.filter((item) => {
      const matchesCollection =
        collectionFilter === 'All' || item.collection === collectionFilter;
      const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
      const searchableText = [
        item.title,
        item.summary,
        item.impact,
        item.description,
        item.collection,
        item.category,
        item.status,
        ...(item.technologies || []),
        ...(item.tags || []),
      ]
        .join(' ')
        .toLowerCase();
      const matchesSearch = !query || searchableText.includes(query);

      return matchesCollection && matchesStatus && matchesSearch;
    });
  }, [collectionFilter, deferredSearch, portfolio, statusFilter]);

  const featuredItems = useMemo(
    () => (portfolio?.items || []).filter((item) => item.featured).slice(0, 4),
    [portfolio]
  );

  const collectionSummary = useMemo(() => {
    if (!portfolio) {
      return [];
    }

    return portfolio.taxonomies.collections.map((collection) => ({
      label: collection,
      count: portfolio.items.filter((item) => item.collection === collection).length,
    }));
  }, [portfolio]);

  const socialEntries = useMemo(() => {
    if (!portfolio) {
      return [];
    }

    return [
      { key: 'github', label: 'GitHub', value: portfolio.profile.github },
      { key: 'linkedin', label: 'LinkedIn', value: portfolio.profile.socials.linkedin },
      { key: 'youtube', label: 'YouTube', value: portfolio.profile.socials.youtube },
      { key: 'tiktok', label: 'TikTok', value: portfolio.profile.socials.tiktok },
      { key: 'facebook', label: 'Facebook', value: portfolio.profile.socials.facebook },
    ];
  }, [portfolio]);

  if (loading && !portfolio) {
    return (
      <div className="state-screen">
        <p className="state-screen__eyebrow">Kyros Code</p>
        <h1>Loading portfolio platform...</h1>
      </div>
    );
  }

  if (error && !portfolio) {
    return (
      <div className="state-screen">
        <p className="state-screen__eyebrow">Connection issue</p>
        <h1>We could not load the portfolio backend.</h1>
        <p>{error}</p>
        <button type="button" className="primary-button" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="portfolio-page">
      <header className="site-header ">
        <a className="brand" href="#top">
          <span className="brand-mark">KC</span>
          <span className="brand-text">
            <strong>{portfolio.profile.brand}</strong>
            <small>{portfolio.profile.name}</small>
          </span>
        </a>

        <nav className="site-nav" aria-label="Primary navigation">
          <a href="#about">About</a>
          <a href="#featured">Featured</a>
          <a href="#library">Library</a>
          <a href="#services">Services</a>
          <a href="#contact">Contact</a>
        </nav>

        <div className="site-header__actions">
          <a className="header-button" href={`mailto:${portfolio.profile.email}`}>
            Hire Me
          </a>
        </div>
      </header>

      <main className="main-content" id="top">
        <section className="hero-section">
          <div className="hero-copy">
            <span className="eyebrow">Founder of {portfolio.profile.brand}</span>
            <h1>{portfolio.profile.name}</h1>
            <p className="hero-role">{portfolio.profile.title}</p>
            <p className="hero-alias">
              Also known as {[...(portfolio.profile.alternateNames || []), portfolio.profile.alias]
                .filter(Boolean)
                .join(' • ')}
            </p>
            <p className="hero-summary">{portfolio.profile.headline}</p>
            <p className="hero-summary hero-summary--secondary">{portfolio.profile.summary}</p>

            <div className="hero-actions">
              <a className="primary-button" href={`mailto:${portfolio.profile.email}`}>
                Hire Me
                <FaArrowRight aria-hidden="true" />
              </a>
              <a className="secondary-button" href="#library">
                View Work
              </a>
            </div>

            <div className="proof-grid">
              {portfolio.profile.stats.map((stat) => (
                <article key={stat.label} className="proof-card">
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </article>
              ))}
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-panel hero-panel--primary">
              <span className="hero-kicker">Now backed by a real content server</span>
              <h3>Public portfolio plus a Mongo-backed backoffice workflow</h3>
              <p>
                The site now supports separate content collections for projects,
                APK releases, desktop apps, open-source code, IoT systems, and blueprint-style
                assets, with richer status tracking for active work and sale-ready
                listings.
              </p>
            </div>

            <div className="hero-highlight-grid">
              {collectionSummary.map((entry) => (
                <article key={entry.label}>
                  <FaPlayCircle aria-hidden="true" />
                  <span>
                    {entry.label}
                    <strong>{entry.count} items</strong>
                  </span>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="content-section" id="about">
          <SectionHeader
            label="About"
            title="Business-driven engineering with product and systems depth"
            description="The focus is still the same: building high-performance software that looks strong, scales well, and solves real business or operational problems."
          />

          <div className="about-grid">
            <article className="glass-panel story-panel">
              {portfolio.profile.about.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </article>

            <div className="value-grid">
              <article className="value-card">
                <h3>Search identity control</h3>
                <p>
                  The public page is now aligned around Davis Nwachukwu, with
                  support for alternate names and the alias {portfolio.profile.alias}
                  across SEO metadata and profile content.
                </p>
              </article>
              <article className="value-card">
                <h3>Status-aware listings</h3>
                <p>
                  Mark work as In Progress, Up For Sale, Sold, Completed, Prototype,
                  or Archived without redesigning the site.
                </p>
              </article>
              <article className="value-card">
                <h3>Media-rich showcase</h3>
                <p>
                  Each item can now carry multiple links, video references, sale
                  notes, GitHub source links, and category-specific metadata.
                </p>
              </article>
              <article className="value-card">
                <h3>Backend-ready growth</h3>
                <p>
                  The platform is no longer locked to browser storage, which makes
                  future upgrades like auth, file uploads, and a stronger LLM chatbot
                  much easier.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="content-section" id="featured">
          <SectionHeader
            label="Featured"
            title="Highlighted work across software, mobile, AI, and IoT"
            description="Featured entries are managed from the studio page so the strongest projects stay front and center."
          />
          <div className="item-grid">
            {featuredItems.map((item) => (
              <ItemCard key={item.id} item={item} />
            ))}
          </div>
        </section>

        <section className="content-section" id="library">
          <SectionHeader
            label="Build Library"
            title="Projects, APKs, open-source code, IoT systems, and blueprints"
            description="Use the filters to explore completed work, sellable assets, in-progress products, and future releases from one shared content system."
          />

          <div className="toolbar-panel">
            <div className="filter-group">
              {collections.map((collection) => (
                <button
                  key={collection}
                  type="button"
                  className={collectionFilter === collection ? 'filter-chip active' : 'filter-chip'}
                  onClick={() => {
                    startTransition(() => {
                      setCollectionFilter(collection);
                    });
                  }}
                >
                  {collection}
                </button>
              ))}
            </div>

            <div className="filter-group filter-group--status">
              {statuses.map((status) => (
                <button
                  key={status}
                  type="button"
                  className={statusFilter === status ? 'filter-chip active' : 'filter-chip'}
                  onClick={() => {
                    startTransition(() => {
                      setStatusFilter(status);
                    });
                  }}
                >
                  {status}
                </button>
              ))}
            </div>

            <label className="search-field">
              <span>Search the stack</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search AI, APK, ESP32, Web3, POS..."
              />
            </label>
          </div>

          <div className="item-grid">
            {filteredItems.map((item) => (
              <ItemCard key={item.id} item={item} />
            ))}
          </div>

          {!filteredItems.length ? (
            <div className="empty-panel">
              <h3>No items match this filter yet.</h3>
              <p>
                Add a new APK, open-source release, blueprint, or device build from
                the backoffice and it will appear here automatically.
              </p>
            </div>
          ) : null}
        </section>

        <section className="content-section" id="services">
          <SectionHeader
            label="Services"
            title="High-value software work for founders, teams, and products"
            description="The portfolio remains grounded in delivery: modern products, strong integrations, and systems that support real growth."
          />

          <div className="service-grid">
            {portfolio.services.map((service) => (
              <article key={service.title} className="service-card">
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="content-section">
          <SectionHeader
            label="Skills"
            title="Technical depth across frontend, backend, mobile, cloud, and advanced systems"
            description="Structured skill groups still keep the public story clear while the backend handles the heavier content management."
          />

          <div className="skill-grid">
            {portfolio.skillGroups.map((group) => (
              <article key={group.title} className="skill-card">
                <h3>{group.title}</h3>
                <div className="chip-list">
                  {group.items.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>



        <section className="content-section" id="contact">
          <SectionHeader
            label="Contact"
            title="Keep space for every channel you want to grow into"
            description="GitHub is live now, and dedicated slots are already waiting for LinkedIn, YouTube, TikTok, and Facebook so you can expand the brand from the backoffice."
            compact
          />

          <div className="contact-grid">
            <a className="contact-card" href={`mailto:${portfolio.profile.email}`}>
              <span className="contact-icon">
                <FaEnvelope aria-hidden="true" />
              </span>
              <span className="contact-meta">
                <strong>Email</strong>
                <span>{portfolio.profile.email}</span>
              </span>
            </a>

            {socialEntries
  .filter((entry) => entry.value)
  .map((entry) => (
    <a
      key={entry.key}
      className="contact-card"
      href={entry.value}
      target={entry.value.startsWith('http') ? '_blank' : undefined}
      rel={entry.value.startsWith('http') ? 'noreferrer' : undefined}
    >
      <span className="contact-icon">{socialIcons[entry.key]}</span>
      <span className="contact-meta">
        <strong>{entry.label}</strong>
        <span>{entry.value}</span>
      </span>
    </a>
  ))}

          </div>
        </section>
      </main>

      <footer className="site-footer">
        <p>{portfolio.profile.brand} platform for {portfolio.profile.name}.</p>
        <p>
          Public portfolio, Mongo-backed backoffice, backend API, and starter chatbot in
          one workspace.
        </p>
      </footer>
              <section className="content-section content-section--chat">
          <ChatbotPanel onSend={onSendChat} />
        </section>
    </div>
  );
}

export default PublicPortfolioPage;
